/**
 * Modal de espera, con la opción de cancelar.
 *
 * La consulta de jornadas cruza GeoVictoria con los TMS y tarda decenas de
 * segundos. Sin esto, la pantalla se queda quieta y no hay forma de saber si
 * está trabajando o si se colgó; y una vez lanzada no se podía abortar.
 *
 * Se muestra el tiempo transcurrido a propósito: una espera larga se tolera
 * mucho mejor cuando se ve que algo avanza, y además da la referencia para
 * decidir si conviene esperar o acotar el rango de fechas.
 *
 * No se usa `Modal` porque acá el cierre NO es libre: la cruz y el clic en el
 * fondo no deben abortar una consulta por accidente. Se cancela apretando
 * Cancelar, o con Escape, que es lo que el navegador ya hace y no tiene
 * sentido pelear.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';

export interface PropsModalCargando {
  readonly abierto: boolean;
  readonly titulo: string;
  /** Qué está pasando, en una línea. */
  readonly detalle?: string;
  /** Qué decir cuando la espera se hace larga. Lo de siempre es la consulta
   *  de jornadas, que es la que tarda; cada pantalla explica lo suyo. */
  readonly notaDemora?: string;
  readonly onCancelar: () => void;
}

/** A partir de acá la espera deja de ser normal y conviene decir algo más. */
const SEGUNDOS_LARGO = 40;

const DEMORA_JORNADAS =
  'Está tardando más de lo habitual. Se consulta GeoVictoria persona por persona, y un ' +
  'rango de varios días lleva su tiempo.';

export function ModalCargando({
  abierto,
  titulo,
  detalle,
  notaDemora = DEMORA_JORNADAS,
  onCancelar,
}: PropsModalCargando): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const [segundos, setSegundos] = useState(0);

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo) return;
    if (abierto && !dialogo.open) dialogo.showModal();
    if (!abierto && dialogo.open) dialogo.close();
  }, [abierto]);

  // El contador arranca de cero en cada consulta, no en cada render.
  useEffect(() => {
    if (!abierto) return;
    setSegundos(0);
    const reloj = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(reloj);
  }, [abierto]);

  return (
    <dialog
      ref={ref}
      className="modal modal--cargando"
      aria-busy="true"
      aria-label={titulo}
      onCancel={(ev) => {
        // Escape cancela, pero pasando por el estado de React: si el <dialog>
        // se cerrara solo, la consulta seguiría corriendo por detrás.
        ev.preventDefault();
        onCancelar();
      }}
    >
      <div className="modal-caja cargando-caja">
        <Loader2 className="girando" size={28} aria-hidden="true" />

        <h2>{titulo}</h2>
        {detalle !== undefined && <p className="nota">{detalle}</p>}

        <p className="cargando-reloj" aria-live="polite">
          {`${String(segundos)} s`}
        </p>

        {segundos >= SEGUNDOS_LARGO && <p className="nota">{notaDemora}</p>}

        <button type="button" className="plano" onClick={onCancelar}>
          <X size={15} aria-hidden="true" />
          Cancelar
        </button>
      </div>
    </dialog>
  );
}
